/**
 * Phase 9.7 — "Pin to brief" cross-surface bus.
 *
 * Operator pins items from any surface (Cori Rt panel, PPI, wastewater,
 * variant tracker, intervention basket, scenario sim) throughout the shift.
 * The Builder (§7.25) reads from this bus to pre-populate the Pack's BLUF
 * + indicator strip when the operator opens the briefing pack.
 *
 * v1 (this) uses module-scope state + a simple subscribe pattern. Phase 9
 * persists via the LocalStorageStore from audit/storage.ts so pins survive
 * reload. CD R10 Q8: lives in the R9 session bar long-term — visible "📌 N
 * pinned" affordance the operator clicks to inspect/edit.
 */

import { auditCapture } from '../audit/capture';

export interface PinnedItem {
  id: string;
  /** Surface section the pin came from (§7.24 / §7.4 / etc.). */
  surface: string;
  /** One-line operator-facing label (becomes a BLUF anchor candidate). */
  label: string;
  /** Numeric value if applicable — surfaces in the BLUF's PHIA suggestion. */
  value?: number | string;
  /** ISO timestamp when pinned. */
  pinnedAt: string;
  /** Arbitrary surface-specific context that the Builder uses for the BLUF
   *  draft + the indicator-strip mapping. */
  context?: Record<string, unknown>;
  /** Phase 9.7 Q10 — operator explicitly excluded this pin from the
   *  rendered pack (kept in queue, greyed-out, line-through). When true,
   *  the BLUF auto-draft + pinned-items panel render the pin as
   *  operator-suppressed instead of operator-added. */
  suppressed?: boolean;
}

const STORAGE_KEY = 'biomax.pack.pins';

function load(): PinnedItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PinnedItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(pins: readonly PinnedItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pins));
  } catch {
    // Quota exceeded / SecurityError — silent drop
  }
}

let pins: PinnedItem[] = load();
type Listener = (next: readonly PinnedItem[]) => void;
const listeners = new Set<Listener>();

export function pinForBrief(item: Omit<PinnedItem, 'pinnedAt'>): void {
  // Deduplicate by id
  if (pins.find(p => p.id === item.id)) return;
  const full: PinnedItem = { ...item, pinnedAt: new Date().toISOString() };
  pins = [full, ...pins];
  persist(pins);
  auditCapture.emit({
    event: { type: 'pin.add', surface: item.surface, subjectIds: [item.id],
      payload: { label: item.label, target: 'minister-briefing-pack' } },
  });
  for (const fn of listeners) fn(pins);
}

export function unpinForBrief(id: string): void {
  const item = pins.find(p => p.id === id);
  if (!item) return;
  pins = pins.filter(p => p.id !== id);
  persist(pins);
  auditCapture.emit({
    event: { type: 'pin.remove', surface: item.surface, subjectIds: [id],
      payload: { target: 'minister-briefing-pack' } },
  });
  for (const fn of listeners) fn(pins);
}

export function listBriefPins(): readonly PinnedItem[] {
  return pins;
}

export function isPinnedForBrief(id: string): boolean {
  return pins.some(p => p.id === id);
}

export function subscribeToBriefPins(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Phase 9.7 Q10 — toggle a pin's suppressed flag. Kept in the queue
 *  (still listed in PinnedItemsPanel + counts towards session-bar chip)
 *  but the BLUF auto-draft + future grouped renders skip it. */
export function toggleSuppressPin(id: string): void {
  const item = pins.find(p => p.id === id);
  if (!item) return;
  const next = pins.map(p => p.id === id ? { ...p, suppressed: !p.suppressed } : p);
  pins = next;
  persist(pins);
  auditCapture.emit({
    event: {
      type: 'pin.add',
      surface: item.surface,
      subjectIds: [id],
      payload: { target: 'minister-briefing-pack', action: next.find(p => p.id === id)?.suppressed ? 'suppress' : 'unsuppress' },
    },
  });
  for (const fn of listeners) fn(pins);
}
